import React from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Card, Statistic, Row, Col, Progress, Tag } from 'antd';
import {
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { Appointment } from '../../services/appointmentApi';
import './ShopOverviewCard.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ShopOverviewCardProps {
  date: Dayjs;
  employees: Employee[];
  appointments: Appointment[];
  schedules: Map<number, TechnicianSchedule[]>;
}

export const ShopOverviewCard: React.FC<ShopOverviewCardProps> = ({
  date,
  employees,
  appointments,
  schedules
}) => {
  const dayOfWeek = date.day();

  // Calculate total working employees today
  const workingEmployees = employees.filter(emp => {
    const empSchedule = schedules.get(emp.id) || [];
    return empSchedule.some(s => s.day_of_week === dayOfWeek && s.is_active);
  });

  // Calculate appointments today
  const todayAppointments = appointments.filter(apt =>
    dayjs(apt.start_time).isSame(date, 'day')
  );

  // Calculate detailed shop metrics
  const calculateShopMetrics = () => {
    let totalAvailableMinutes = 0;
    let totalBookedMinutes = 0;
    let totalOpenMinutes = 0;

    workingEmployees.forEach(emp => {
      const empSchedule = schedules.get(emp.id) || [];
      const todaySchedule = empSchedule.find(s => s.day_of_week === dayOfWeek && s.is_active);

      if (todaySchedule) {
        const [startHour, startMinute] = todaySchedule.start_time.split(':').map(Number);
        const [endHour, endMinute] = todaySchedule.end_time.split(':').map(Number);
        const workMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);

        totalAvailableMinutes += workMinutes;

        // Calculate booked time for this employee
        const empAppointments = todayAppointments.filter(apt => {
          if (apt.technician_id === emp.id) return true;
          if (apt.technicians?.some(t => t.technician_id === emp.id)) return true;
          return false;
        });

        let bookedMinutes = 0;
        empAppointments.forEach(apt => {
          const start = dayjs(apt.start_time);
          const end = dayjs(apt.end_time);
          const duration = end.diff(start, 'minutes');
          bookedMinutes += duration;
        });

        totalBookedMinutes += bookedMinutes;
        totalOpenMinutes += (workMinutes - bookedMinutes);
      }
    });

    const capacity = totalAvailableMinutes === 0 ? 0 : Math.min((totalBookedMinutes / totalAvailableMinutes) * 100, 100);

    return {
      capacity,
      totalOpenMinutes,
      totalBookedMinutes,
      totalAvailableMinutes
    };
  };

  const metrics = calculateShopMetrics();
  const openHours = Math.floor(metrics.totalOpenMinutes / 60);
  const openMinutes = metrics.totalOpenMinutes % 60;

  // Get capacity status
  const getCapacityStatus = (): { color: string; text: string } => {
    if (metrics.capacity < 50) return { color: 'success', text: 'Plenty of Availability' };
    if (metrics.capacity < 75) return { color: 'warning', text: 'Moderate Bookings' };
    if (metrics.capacity < 90) return { color: 'orange', text: 'Busy Day' };
    return { color: 'error', text: 'Nearly Full' };
  };

  const status = getCapacityStatus();

  return (
    <Card
      className="shop-overview-card"
      title={
        <div className="shop-overview-title">
          <TeamOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700 }}>Shop Overview</div>
            <div style={{ fontSize: '14px', fontWeight: 400, opacity: 0.85 }}>
              {date.format('dddd, MMMM D, YYYY')}
            </div>
          </div>
        </div>
      }
      variant="borderless"
    >
      {/* Stats Row */}
      <Row gutter={[8, 0]}>
        <Col xs={12} sm={6}>
          <Statistic
            title="Working Today"
            value={workingEmployees.length}
            suffix={`/ ${employees.length}`}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#1890ff', fontSize: '24px' }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Appointments"
            value={todayAppointments.length}
            prefix={<CalendarOutlined />}
            valueStyle={{ color: '#52c41a', fontSize: '24px' }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Open Time"
            value={`${openHours}h ${openMinutes}m`}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#faad14', fontSize: '20px' }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Capacity"
            value={metrics.capacity.toFixed(0)}
            suffix="%"
            prefix={<CheckCircleOutlined />}
            valueStyle={{
              color: status.color === 'success' ? '#52c41a' :
                     status.color === 'warning' ? '#faad14' :
                     status.color === 'orange' ? '#fa8c16' : '#ff4d4f',
              fontSize: '24px'
            }}
          />
        </Col>
      </Row>

      {/* Capacity Bar */}
      <div className="shop-capacity-section">
        <div className="capacity-header">
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#595959' }}>
            Shop Workload
          </span>
          <Tag
            color={status.color}
            style={{ fontSize: '13px', padding: '4px 12px', fontWeight: 600 }}
          >
            {status.text}
          </Tag>
        </div>
        <Progress
          percent={metrics.capacity}
          strokeColor={{
            '0%': status.color === 'success' ? '#52c41a' :
                  status.color === 'warning' ? '#faad14' :
                  status.color === 'orange' ? '#fa8c16' : '#ff4d4f',
            '100%': status.color === 'success' ? '#95de64' :
                    status.color === 'warning' ? '#ffc53d' :
                    status.color === 'orange' ? '#ffd591' : '#ff7875'
          }}
          trailColor="#f0f0f0"
          strokeWidth={8}
          showInfo={false}
        />
      </div>

      {/* Quick Info */}
      {workingEmployees.length === 0 && (
        <div className="shop-alert">
          <Tag color="red" style={{ fontSize: '14px', padding: '8px 16px' }}>
            No employees scheduled to work today
          </Tag>
        </div>
      )}
    </Card>
  );
};
